
import { addDays } from "date-fns";
import _ from "lodash";
import { useCallback, useReducer } from "react";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { CustomDurationDialogValues } from "../../components/custom-duration-dialog/CustomDurationDialog";
import CustomDurationUtils, { DurationOptionEnum } from "../../components/custom-duration-dialog/CustomDurationUtils";
import { IFormTemplate} from "../../services/custom-forms-data-service/TemplateModels";
import { IFormInstanceData } from "../../views/object-form-view/Models";
import { CustomFormRunnerModeType } from "../custom-form-runner/CustomFormRunner";
import { actionItems, CustomFormListActionEnum } from "./CustomFormInstanceListActions";
import { ALL_FORM_TEMPLATES_ID } from "./CustomFormInstanceListUtils";
import { ActionMenuItem } from "../action-menu/ActionMenuItem";

export type ContentFormInstanceListStateType = {
    mode: CustomFormRunnerModeType;
    templates: IFormTemplate[];
    filteredTemplates: IFormTemplate[];
    availableObjects: IeLynxObject[] | undefined,
    actions: ActionMenuItem[],
    durationLabel: string;
    durationOptions: CustomDurationDialogValues;
    formRunnerOpen: boolean;
    templateToRun: IFormTemplate | undefined;
    instanceData: IFormInstanceData | undefined;
    isDirty: boolean;
    sessionToken?: string;
    downloadUid: string | undefined;
    showNoTemplateDialog: boolean;
};

let defaultCustomFormInstanceListState: ContentFormInstanceListStateType = {
    mode: 'add',
    actions: actionItems,
    //durationLabel: i18n.t('customDuration.30Days'),
    durationLabel: 'Default - 30 Days',
    durationOptions: {
        option: DurationOptionEnum.ThirtyDays,
        duration: {
            startDate: addDays(new Date(), -30),
            endDate: undefined
        }
    },
    formRunnerOpen: false,
    templates: [],
    filteredTemplates: [],
    availableObjects: undefined,
    templateToRun: undefined,
    instanceData: undefined,
    isDirty: false,
    downloadUid: undefined,
    showNoTemplateDialog: false
};

enum CustomFormInstanceListStateActionType {
    ModeChanged = 'CUSTOM_FORM_LIST_MODE_CHANGED',
    DurationChanged = 'CUSTOM_FORM_LIST_DURATION_CHANGED',
    TemplatesChanged = 'CUSTOM_FORM_LIST_TEMPLATES_CHANGED',
    FormRunnerOpenChanged = 'CUSTOM_FORM_LIST_RUNNER_OPEN_CHANGED',
    TemplateToRunChanged = 'CUSTOM_FORM_LIST_TEMPLATE_TO_RUN_CHANGED',
    InstanceDataChanged = 'CUSTOM_FORM_LIST_INSTANCE_DATA_CHANGED',
    AvailableObjectsChanged = 'CUSTOM_FORM_AVAIALBLE_OBJECTS_CHANGED',
    DownloadUidChanged = 'CUSTOM_FORM_DOWNLOAD_UID_CHANGED',    
    ShowNoTemplateDialogChanged = 'CUSTOM_FORM_SHOW_NO_TEMPLATE_CHANGED',    
    Reset = 'CUSTOM_FORM_LIST_RESET'
}

type CustomFormInstanceListStateActions = | {
    type: CustomFormInstanceListStateActionType.DurationChanged;
    payload: CustomDurationDialogValues;
} | {
    type: CustomFormInstanceListStateActionType.ModeChanged;
    payload: CustomFormRunnerModeType;
} | {
    type: CustomFormInstanceListStateActionType.TemplatesChanged;
    payload: IFormTemplate[];
} | {
    type: CustomFormInstanceListStateActionType.AvailableObjectsChanged;
    payload: IeLynxObject[] | undefined;
} | {
    type: CustomFormInstanceListStateActionType.InstanceDataChanged;
    payload: IFormInstanceData | undefined;
} | {
   type: CustomFormInstanceListStateActionType.FormRunnerOpenChanged;
   payload: boolean;
} | {
   type: CustomFormInstanceListStateActionType.TemplateToRunChanged;
   payload: IFormTemplate | undefined;
} | {
    type: CustomFormInstanceListStateActionType.DownloadUidChanged;
    payload: string | undefined;
} | {
    type: CustomFormInstanceListStateActionType.ShowNoTemplateDialogChanged;
    payload: boolean;
} | {
    type: CustomFormInstanceListStateActionType.Reset;
    payload: void;
};

const isDirty = (newState: ContentFormInstanceListStateType, 
                defaultState: ContentFormInstanceListStateType) => 
                newState.durationLabel !== defaultState.durationLabel || 
                newState.templates.find(o => o.selected)?.id !== ALL_FORM_TEMPLATES_ID;

function objectFormViewReducer(state: ContentFormInstanceListStateType, action: CustomFormInstanceListStateActions): ContentFormInstanceListStateType {

    const copy = {...state};
    switch (action.type) {
        
        case CustomFormInstanceListStateActionType.ModeChanged:
            copy.mode = action.payload;
            return copy;

        case CustomFormInstanceListStateActionType.DurationChanged:
            copy.durationLabel = action.payload.option === DurationOptionEnum.ThirtyDays ? 
                    'Default - 30 Days' : 
                    CustomDurationUtils.getCustomDurationLabel(action.payload.option, true);
            copy.durationOptions = action.payload;
            if (copy.durationOptions.option !== DurationOptionEnum.Custom) {
                copy.durationOptions.duration.endDate = undefined;
            }

            copy.isDirty = isDirty(copy, {...defaultCustomFormInstanceListState});
            return copy;

        case CustomFormInstanceListStateActionType.TemplatesChanged:
            copy.templates = action.payload;
            copy.actions = _.cloneDeep(state.actions);
            const addFormData = copy.actions.find(o => o.action === CustomFormListActionEnum.AddFormData);
            if (addFormData) {
                addFormData.subActions = copy.templates
                                .filter(o => o.id !== ALL_FORM_TEMPLATES_ID && !o.isDeleted)
                                .map((o, index) => {
                                    return {
                                    id: `subaction-${index}`,
                                    label: o.name,
                                    action: o.name

                                }});
            }

            copy.isDirty = copy.templates.find(o => o.selected)?.id !== ALL_FORM_TEMPLATES_ID;
            copy.filteredTemplates = copy.isDirty ? copy.templates.filter(o => o.id !== ALL_FORM_TEMPLATES_ID && o.selected) : copy.templates;
            return copy;

        case CustomFormInstanceListStateActionType.InstanceDataChanged:
            copy.instanceData = action.payload ? {...action.payload} : undefined;
            return copy;

        case CustomFormInstanceListStateActionType.AvailableObjectsChanged:
            copy.availableObjects = action.payload;
            return copy;

        case CustomFormInstanceListStateActionType.Reset:
            return {...defaultCustomFormInstanceListState};
    
        case CustomFormInstanceListStateActionType.FormRunnerOpenChanged:
            copy.formRunnerOpen = action.payload;
            return copy;

        case CustomFormInstanceListStateActionType.TemplateToRunChanged:
            copy.templateToRun = action.payload;
            return copy;

        case CustomFormInstanceListStateActionType.DownloadUidChanged:
            copy.downloadUid = action.payload;
            return copy;

        case CustomFormInstanceListStateActionType.ShowNoTemplateDialogChanged:
            copy.showNoTemplateDialog = action.payload;
            return copy;
    
        default:
            return state;
    }
}

export default function useCustomFormInstanceListState() {

    const [state, dispatch] = useReducer(objectFormViewReducer, {...defaultCustomFormInstanceListState});

    const setMode = useCallback((value: CustomFormRunnerModeType) => dispatch({
        type: CustomFormInstanceListStateActionType.ModeChanged,
        payload: value
    }), []);

    const setDuration = useCallback((value: CustomDurationDialogValues) => dispatch({
        type: CustomFormInstanceListStateActionType.DurationChanged,
        payload: value
    }), []);

    const setTemplates = useCallback((value: IFormTemplate[]) => dispatch({
        type: CustomFormInstanceListStateActionType.TemplatesChanged,
        payload: value
    }), []);

    const setFormRunnerOpen = useCallback((value: boolean) => dispatch({
        type: CustomFormInstanceListStateActionType.FormRunnerOpenChanged,
        payload: value
    }), []);    

    const setTemplateToRun = useCallback((value: IFormTemplate | undefined) => dispatch({
        type: CustomFormInstanceListStateActionType.TemplateToRunChanged,
        payload: value
    }), []);    


    const setInstanceData = useCallback((value: IFormInstanceData | undefined) => dispatch({
        type: CustomFormInstanceListStateActionType.InstanceDataChanged,
        payload: value
    }), []);

    const setAvailableObjects = useCallback((value: IeLynxObject[] | undefined) => dispatch({
        type: CustomFormInstanceListStateActionType.AvailableObjectsChanged,
        payload: value
    }), []);    

    const resetState = useCallback(() => dispatch({
        type: CustomFormInstanceListStateActionType.Reset,
        payload: undefined
    }), []);

    const setDownloadUid = useCallback((value: string | undefined) => dispatch({
        type: CustomFormInstanceListStateActionType.DownloadUidChanged,
        payload: value
    }), []);

    const setShowNoTemplateDialog = useCallback((value: boolean) => dispatch({
        type: CustomFormInstanceListStateActionType.ShowNoTemplateDialogChanged,
        payload: value
    }), []);

    return {
        state,
        setMode,
        setDuration,
        setFormRunnerOpen,
        setTemplateToRun,
        setInstanceData,
        setTemplates,
        setAvailableObjects,
        setDownloadUid,
        setShowNoTemplateDialog,
        resetState
    };
}

